import * as THREE from 'three';
import type { CombatVisualEvent } from '../../../shared/game/combat-types.js';
import type { CastState } from '../../../shared/game/class-types.js';
import type { HeroState, UnitState } from '../../../shared/game/world-types.js';
import type { UnitAnimator } from '../entities/unit-animator.js';
import { schoolOfAbility } from '../../../content/spells/schools.js';
import { ACTION_CAST_SECONDS } from '../../../shared/game/class-types.js';
import { isCantripAction } from '../entities/character-animator.js';

interface PreviewCast { event: CombatVisualEvent; abilityId: string; at: number; seconds: number; targets: string[] }
/** Authoring cast projection. It reuses the real animator and channel renderer without changing a server snapshot. */
export class ChanneledPreviews {
  private pending = new Map<string, PreviewCast>();
  static ability(event: CombatVisualEvent) {
    if (event.kind === 'arc' || event.abilityId === 'preview-lightning') return 'channel-lightning';
    return event.abilityId && schoolOfAbility(event.abilityId) ? event.abilityId : undefined;
  }
  begin(event: CombatVisualEvent, time: number, seconds = 2, targets: string[] = []) {
    const abilityId = ChanneledPreviews.ability(event);
    if (!abilityId || this.pending.has(event.actorId)) return false;
    // A cantrip is always one action; only a spell's channel can be stretched.
    this.pending.set(event.actorId, { event, abilityId, at: time, seconds: isCantripAction(abilityId) ? ACTION_CAST_SECONDS : THREE.MathUtils.clamp(seconds, 2, 6),
      targets: [...new Set([event.targetId, ...targets])] }); return true;
  }
  project<T extends UnitState>(state: T, time: number): T {
    const preview = this.pending.get(state.id);
    if (!preview || !('class' in state)) return state;
    const hero = state as unknown as HeroState;
    if (hero.health.dead || hero.health.dying) { this.pending.delete(state.id); return state; }
    return { ...state, class: { ...hero.class, casting: this.cast(preview, time) } };
  }
  private cast(preview: PreviewCast, time: number): CastState {
    return { abilityId: preview.abilityId, targetId: preview.event.targetId, duration: preview.seconds,
      remaining: Math.max(0, preview.seconds - Math.max(0, time - preview.at)) };
  }
  casts(time: number) { return new Map([...this.pending].map(([id, preview]) => [id, this.cast(preview, time)])); }
  /** Called after posing the completion frame, so release uses the collapsed orb's (or the cantrip hand's) exact world position. */
  release(time: number, animators: ReadonlyMap<string, UnitAnimator>, positions: ReadonlyMap<string, THREE.Object3D>,
    emit: (event: CombatVisualEvent, origin: THREE.Vector3) => void) {
    for (const [id, preview] of this.pending) {
      if (!positions.get(id)?.visible) { this.pending.delete(id); continue; }
      if (time + 1e-8 < preview.at + preview.seconds) continue;
      const origin = animators.get(id)?.spellOrigin?.();
      if (origin) for (const targetId of preview.targets) if (positions.get(targetId)?.visible) emit({ ...preview.event, targetId }, origin.clone());
      this.pending.delete(id);
    }
  }
  clear() { this.pending.clear(); }
}
